import classNames from "classnames";
import React, { FC, useMemo, useState } from "react";
import { Loader } from "shared/ui";
import { notification } from "antd";
import { useTranslation } from "react-i18next";
import { handleRequestError, renderDynamicSelectValues } from "shared/helpers";
import { AdaptedValues, InitialInputMaterials } from "shared/types";
import {
  InputMaterialCategory,
  MaterialStockItem,
  ProductionInputMaterial,
  StockMaterialItem,
  useUpdateMaterialItemStatusToUsedMutation,
} from "shared/api";
import { useConfirmDeleteModal } from "shared/hooks";
import { variantSelectValues } from "shared/constants";
import { SelectValueDropdown } from "./SelectValueDropdown";
import { DynamicInputMaterialVariant } from "./DynamicInputMaterialVariant";
import { DynamicInputMaterialQuantity } from "./DynamicInputMaterialQuantity";
import { AddProductionRequest } from "./ProcessStepProduction";

interface ProductionInputMaterialsFormProps {
  values: AddProductionRequest;
  inputIndex: number;
  fieldIndex: number;
  adaptedMaterials: AdaptedValues[];
  adaptedMaterialItems: AdaptedValues[];
  adaptedStockMaterialBatches: AdaptedValues[];
  adaptedInitialInputMaterials: InitialInputMaterials[];
  materialStockBatches?: StockMaterialItem[];
  materialItems?: MaterialStockItem[];
  moreThanOneMaterial: boolean;
  isLoadingInputMaterialsData?: boolean;
  inputMaterial: ProductionInputMaterial;
  removeCard: <T>(index: number) => T | undefined;
  dynamicInputMaterialCategory?: InputMaterialCategory;
  isUnlocked: Record<number, boolean>;
  toggleUnlockField: (inputIndex: number) => void;
}

const ProductionInputMaterialsForm: FC<ProductionInputMaterialsFormProps> = ({
  values,
  inputIndex,
  fieldIndex,
  adaptedInitialInputMaterials,
  adaptedMaterialItems,
  adaptedMaterials,
  adaptedStockMaterialBatches,
  materialItems,
  materialStockBatches,
  moreThanOneMaterial,
  isLoadingInputMaterialsData,
  inputMaterial,
  removeCard,
  dynamicInputMaterialCategory,
  isUnlocked,
  toggleUnlockField,
}) => {
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const [showAll, setShowAll] = useState(false);
  const [selectedIdForStatus, setSelectedIdForStatus] = useState<number | null>(
    null
  );
  const [updateStatus] = useUpdateMaterialItemStatusToUsedMutation();

  const dynamicStockBatchValue = useMemo(
    () =>
      renderDynamicSelectValues(
        values,
        inputIndex,
        fieldIndex,
        adaptedMaterials,
        adaptedMaterialItems,
        adaptedStockMaterialBatches,
        adaptedInitialInputMaterials?.[inputIndex],
        materialStockBatches!,
        materialItems!,
        showAll
      ),
    [
      values,
      inputIndex,
      fieldIndex,
      adaptedMaterials,
      adaptedMaterialItems,
      adaptedStockMaterialBatches,
      adaptedInitialInputMaterials,
      materialStockBatches,
      materialItems,
      showAll,
    ]
  );

  const handleMarkAsUsed = () => {
    if (!selectedIdForStatus) return;

    const findSelectorLabel = dynamicStockBatchValue.find(
      (element) => element.value === selectedIdForStatus
    );

    const confirmDelete = useConfirmDeleteModal<number>(
      `Are you sure you want to mark ${findSelectorLabel?.label}?`,
      async (id) => {
        try {
          await updateStatus({ id }).unwrap();
          api.success({
            message: t("orders.success"),
            description: t("orders.marked-as-used-msg"),
          });
        } catch (error) {
          const description = handleRequestError(error, t("orders.error"));
          api.error({
            message: t("orders.error"),
            description,
          });
        }
      }
    );

    confirmDelete(selectedIdForStatus);
  };

  return (
    <>
      {contextHolder}
      <div
        key={`${fieldIndex} `}
        className={classNames(
          "flex gap-y-1 gap-x-3 w-full items-center justify-between",
          {
            "!w-[32.9%] flex-col p-2 bg-gray-50 shadow-dynamicFormCard rounded-xl":
              moreThanOneMaterial,
          }
        )}
      >
        <SelectValueDropdown
          inputIndex={inputIndex}
          fieldIndex={fieldIndex}
          variantSelectValues={variantSelectValues}
          className={moreThanOneMaterial ? "w-full" : ""}
          dynamicInputMaterialCategory={dynamicInputMaterialCategory}
        />

        {isLoadingInputMaterialsData ? (
          <Loader />
        ) : (
          <>
            <DynamicInputMaterialVariant
              moreThanOneMaterial={moreThanOneMaterial}
              inputIndex={inputIndex}
              fieldIndex={fieldIndex}
              dynamicStockBatchValue={dynamicStockBatchValue}
              adaptedInitialInputMaterials={adaptedInitialInputMaterials}
              setSelectedIdForStatus={setSelectedIdForStatus}
            />

            <div className="flex items-center flex-col gap-3">
              <label
                htmlFor="showAll"
                className="flex items-center gap-2 px-3 py-1.5 border border-black rounded-full cursor-pointer transition-all hover:bg-gray-100"
              >
                <input
                  id="showAll"
                  type="checkbox"
                  checked={showAll}
                  onChange={() => setShowAll(!showAll)}
                  className="cursor-pointer"
                />
                {t("orders.view-all")}
              </label>

              <button
                onClick={handleMarkAsUsed}
                type="button"
                className="p-2 bg-green-600 text-white rounded-full cursor-pointer whitespace-nowrap"
              >
                {t("orders.mark-as-used")}
              </button>
            </div>
          </>
        )}

        <DynamicInputMaterialQuantity
          isUnlocked={isUnlocked}
          toggleUnlockField={toggleUnlockField}
          moreThanOneMaterial={moreThanOneMaterial}
          inputIndex={inputIndex}
          fieldIndex={fieldIndex}
          inputMaterial={inputMaterial}
          removeItem={removeCard}
        />
      </div>
    </>
  );
};

export { ProductionInputMaterialsForm };
